import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// Import schemas from generated
const { CreateSpendingDto, SpendingFirestoreResponse } = schemas;

// ====== Generated Schemas ======
export const SpendingResponseSchema = SpendingFirestoreResponse;
export const CreateSpendingSchema = CreateSpendingDto;

// ====== Type Exports ======
export type SpendingResponse = z.infer<typeof SpendingResponseSchema>;
export type CreateSpending = z.infer<typeof CreateSpendingSchema>;

/**
 * Create spendings API factory
 */
export function createSpendingsApi(client: TypedApiClient) {
  return {
    /**
     * Get spendings with optional filters
     */
    getSpendings: (params?: {
      storeId?: string;
      category?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }): Promise<SpendingResponse[]> => {
      return client.get('/spendings', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(SpendingResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/spendings` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getSpendingsWindow: (
      params: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof SpendingResponseSchema>>> => {
      return client.get('/spendings/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(SpendingResponseSchema),
      });
    },

    /**
     * Get a single spending by ID
     */
    getSpendingById: (id: string): Promise<SpendingResponse> => {
      return client.get(apiPath`/spendings/${id}`, {
        responseSchema: SpendingResponseSchema,
      });
    },

    /**
     * Create a new spending
     */
    createSpending: (data: CreateSpending): Promise<SpendingResponse> => {
      return client.post('/spendings', {
        data,
        requestSchema: CreateSpendingSchema,
        responseSchema: SpendingResponseSchema,
      });
    },

    /**
     * Delete a spending
     */
    deleteSpending: (id: string) => {
      return client.delete(apiPath`/spendings/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },
  };
}
